import { Card } from "@/src/components/cards";
import { openDrawer } from "@/src/components/dialogs";
import { Input } from "@/src/components/universalInput";
import {
  Field,
  FolderItem,
  MesureSession,
  Patient,
  RefAnswer,
  Reference,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import { getUserId, jsonAbstract, sleep, uid } from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { getStandardConfidentiality } from "../patients/confidentialitiesHook";

export async function openGrowthInputs({
  patient,
  parent,
  onConfirm,
}: {
  patient: Patient;
  parent: FolderItem | null | undefined;
  onConfirm: (mesures: RefAnswer[]) => void;
}) {
  let _date: string | null = null;
  let mesures: RefAnswer[] = [];

  await openDrawer({
    title: "Ajouter une mesure",
    trailing: <div></div>,
    content: (
      <GrowthInputs
        patient={patient}
        current={mesures}
        date={_date || moment().format("YYYY-MM-DD")}
        onDateChange={(newDate) => {
          _date = newDate;
        }}
        onChange={(newMesures) => {
          mesures = newMesures;
        }}
      ></GrowthInputs>
    ),
    onConfirm: async () => {
      let mesureSession: MesureSession = {
        type: "mesureSession",
        patient,
        creator: getUserId(),
        parent,
        mesures,
        confidentiality: getStandardConfidentiality(patient),
        readBy: [getUserId()],
        _date: _date,
      };

      mesureSession?.mesures?.forEach((mesure) => {
        if (!mesure) return;
        mesure._date = _date;
      });

      await apiFetch(
        "patientMesures",
        "addMesureSession",
        {
          mesureSession,
        },
        "Erreur lors de la création de la session de mesures"
      );

      onConfirm(mesures);
    },
  });
}

export function GrowthInputs({
  mesureReferences,
  date,
  onDateChange,
  patient,
  current,
  onChange,
}: {
  mesureReferences?: Reference[];
  date: string;
  onDateChange: (newDate: string) => void;
  patient: Patient;
  current?: RefAnswer[];
  onChange: (newValue: RefAnswer[]) => void;
}) {
  const allowedReferencesNames = [
    "taille",
    "poids",
    "périmètre cranien",
    "bmi",
  ];
  const [calculatedReference, setCalculatedReference] =
    useState<Reference | null>(null);
  let [searchKey, setSearchKey] = useState("");
  let [refAnswers, setRefAnswers] = useState<RefAnswer[]>(current || []);
  let {
    data: references,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["MesuresInputs", searchKey],
    queryFn: () => {
      if (mesureReferences) return mesureReferences;
      let frequent = USER?.get()?.speciality?.frequentReferences?.filter(
        (reference) => reference?.category == "mesure"
      );

      if (frequent?.length) return frequent;
      return apiFetch("references", "loadMany", {
        userId: getUserId(),
        categoriesIds: ["mesure"],
      });
    },
  });

  references = references?.filter((reference) => reference);

  function getFieldValue(
    referenceName: "taille" | "poids" | "imc" | "surface" | "créatinine"
  ) {
    let value = refAnswers.find(
      (refAnswer) =>
        refAnswer?.reference?.name?.toLowerCase()?.trim() ==
        referenceName?.toLowerCase()?.trim()
    )?.fieldAnswers?.[0]?.value;

    return value;
  }

  async function calculateBMI() {
    let reference = references?.find((reference) => {
      return reference?.name?.toLowerCase()?.trim() == "bmi";
    });
    if (!reference) return;
    let field = reference.sections?.[0]?.fields?.[0];
    if (!field) return;

    let height = getFieldValue("taille") || 0;
    let weight = getFieldValue("poids") || 0;
    if (!height && !weight) return;

    let value = parseFloat(
      ((weight / height / height) * 10000)?.toString()
    ).toFixed(2);

    await onFieldChange(reference, field, value);
    setCalculatedReference(reference);
    await sleep(10);
  }

  async function onFieldChange(
    reference: Reference,
    field: Field,
    newValue: any
  ) {
    let newRefAnswers = [...refAnswers];
    let refAnswer = newRefAnswers.find(
      (refAnswer) => refAnswer.reference?.id == reference.id
    );

    if (!refAnswer) {
      refAnswer = {
        patient,
        creator: getUserId(),
        reference: reference,
        fieldAnswers: [],
      };
      newRefAnswers.push(refAnswer!);
    }

    let fieldAnswer = refAnswer?.fieldAnswers?.find(
      (fieldAnswer) => fieldAnswer?.field?.id == field.id
    );
    if (fieldAnswer) {
      fieldAnswer.value = newValue;
    } else {
      refAnswer?.fieldAnswers?.push({
        field,
        value: newValue,
        parent: null,
        patient,
        creator: getUserId(),
      });
    }

    refAnswers = newRefAnswers;
    setRefAnswers(newRefAnswers);
    onChange(newRefAnswers);

    let referenceName = reference.name?.toLowerCase()?.trim();
    if (
      referenceName == "taille" ||
      referenceName == "poids" ||
      referenceName == "créatinine"
    ) {
      await calculateBMI();
    }
  }

  return (
    <div className="grid c1 mc1 gap15 calign">
      {references
        ?.filter((reference) => {
          return allowedReferencesNames.includes(
            reference?.name?.toLowerCase()?.trim() || ""
          );
        })
        ?.map((reference, index) => {
          if (!reference) return null;
          let refAnswer = refAnswers.find(
            (refAnswer) => refAnswer.reference?.id == reference.id
          );
          let hasValue = refAnswer?.fieldAnswers?.some((fieldAnswer) => {
            return fieldAnswer?.value;
          });
          return (
            <div
              className="grid c1 gap5 mtop10"
              key={`${reference.id}${
                calculatedReference?.id == reference.id ? uid() : ""
              }`}
            >
              {reference.sections?.map((section) => {
                if (!section) return null;
                return (
                  <div key={section.id} className="c1 gap10">
                    <div className="bold">{reference.name}</div>
                    {reference.sections!.length > 1 ? (
                      <div>{section.name}</div>
                    ) : null}
                    <div className="grid gap10" style={{ alignItems: "end" }}>
                      {section.fields?.map((field) => {
                        if (!field) return null;

                        if (field.name == "Nouvel élément") return null;
                        let fieldAnswer = refAnswer?.fieldAnswers?.find(
                          (fieldAnswer) => fieldAnswer?.field?.id == field.id
                        );

                        return (
                          <Input
                            key={field.id}
                            shouldFocus={index == 0}
                            name={
                              section.fields!.length > 1 ? field.name : null
                            }
                            unitName={field.unit?.name}
                            value={fieldAnswer?.value}
                            type={jsonAbstract(field.type)}
                            options={field?.options
                              ?.filter((a) => a)
                              ?.map((option) => {
                                return {
                                  value: option!.id,
                                  label: option!.name,
                                };
                              })}
                            onChange={async (newValue) => {
                              await onFieldChange(reference, field, newValue);
                            }}
                          ></Input>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      <Input
        name={"Date"}
        type="date"
        value={date || moment().format("YYYY-MM-DD")}
        onChange={async (newValue) => {
          onDateChange(newValue);
        }}
      ></Input>
    </div>
  );
}
